import React, {useEffect, useState} from 'react'
import {
    Drawer,
    DrawerOverlay,
    Button,
    Text,
    DrawerContent,
    DrawerBody,
    DrawerHeader,
    DrawerFooter,
    Stack,
    useStyleConfig,
    useBreakpointValue,
    Flex,
    RadioGroup,
    Radio,
    CheckboxGroup,
} from '@chakra-ui/react'
import Checkbox from '../../../components/checkbox'
import {CloseIcon} from '../../../components/icons'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import FreeItemPromoTile from './free-item-promo-tile'
import {remCalc} from '../../../theme/_utils'
import {
    useShopperBasketsMutationHelper,
    useAccessToken,
    useCommerceApi,
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import LoadingSpinner from '../../../components/loading-spinner'

const FreeItemPromoDrawer = ({
    onClose,
    isOpen,
    setSelectedFreeItems,
    selectedPromotion = {},
    selectedFreeItems = {},
    basketItems = [],
    handleRemoveItem,
}) => {
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {refetch} = useCurrentBasket()
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const styles = useStyleConfig('ProductFilterDrawer', {variant: 'freeItemPromo'})
    const drawerPlacement = useBreakpointValue({base: 'bottom', md: 'right'})

    const [promoProducts, setPromoProducts] = useState([])
    const [thisPromoItems, setThisPromoItems] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    const arrChunker = (arr, size = 24) => {
        const chunks = []
        for (let i = 0; i < arr.length; i += size) {
            chunks.push(arr.slice(i, i + size))
        }
        return chunks
    }

    const fetchProducts = async () => {
        try {
            setIsLoading(true)
            const ids = selectedPromotion?.bonusProducts?.map((p) => p?.productId) || []
            const chunkedIds = arrChunker(ids, 24)
            const token = await getTokenWhenReady()

            const responses = await Promise.all(
                chunkedIds.map((batch) =>
                    api.shopperProducts.getProducts({
                        parameters: {
                            ids: batch.join(','),
                        },
                        expand: ['availability', 'images'],
                        headers: {Authorization: `Bearer ${token}`},
                    }),
                ),
            )
            const products = responses.flatMap((res) => res?.data || [])
            const productsWithStock = products?.filter((product) => {
                return product?.inventory?.stockLevel > 0
            })
            setIsLoading(false)
            return productsWithStock
        } catch (e) {
            setIsLoading(false)
            console.error('Error getting products for promotion', e)
            return []
        }
    }

    const refreshBasket = async () => {
        try {
            await refetch()
        } catch (error) {
            console.error('Failed to refresh basket', error)
        }
    }

    useEffect(() => {
        if (!selectedPromotion?.id) return

        const init = async () => {
            await refreshBasket()

            const res = await fetchProducts()
            if (res?.length) {
                setPromoProducts(res)
                const preselectedItems = selectedFreeItems?.[selectedPromotion.id]?.items || []
                setThisPromoItems(preselectedItems)
            }
        }

        init()
    }, [selectedPromotion])

    const promotionId = selectedPromotion?.id || ''
    const allowedItems = selectedPromotion?.maxBonusItems || 0
    const isSingleSelect = allowedItems === 1

    const handleSelect = (product) => {
        if (isSingleSelect) {
            setThisPromoItems([product])
            return
        }

        setThisPromoItems((prev) => {
            const exists = prev.some((item) => item.id === product.id)

            if (exists) {
                const basketPromoItem = basketItems.find(
                    (item) =>
                        item.productId === product.id &&
                        item?.bonusDiscountLineItemId === promotionId,
                )
                if (basketPromoItem) {
                    handleRemoveItem(
                        basketPromoItem,
                        basketPromoItem?.c_variantIdentifier ||
                            basketPromoItem?.name ||
                            basketPromoItem?.productName,
                        100,
                        false,
                    )
                }
                return prev.filter((item) => item.id !== product.id)
            } else {
                if (prev.length < allowedItems) {
                    return [...prev, product]
                }
                return prev
            }
        })
    }

    const handleAddGiftToCart = async () => {
        const selectedItems = thisPromoItems || []
        if (!selectedItems.length) return

        const allProductItems = selectedItems.flatMap((item) => {
            const itemId = item.id || item.productId
            const alreadyInBasket = basketItems.some(
                (basketItem) =>
                    basketItem.productId === itemId &&
                    basketItem?.bonusDiscountLineItemId === promotionId,
            )

            return alreadyInBasket
                ? []
                : [
                      {
                          productId: itemId,
                          price: item.price,
                          quantity: 1,
                          bonusDiscountLineItemId: promotionId,
                          bonusProductLineItem: item?.bonusProductLineItem,
                          c_MaterialGroup: item?.c_MaterialGroup,
                          c_subModelIdentifier: item?.c_subModelIdentifier,
                      },
                  ]
        })
        if (!allProductItems.length) return
        try {
            await addItemToNewOrExistingBasket(allProductItems)

            setSelectedFreeItems((prev) => ({
                ...prev,
                [promotionId]: {
                    ...prev[promotionId],
                    items: thisPromoItems,
                },
            }))
        } catch (e) {
            console.error('Failed to add free items to basket', e)
        } finally {
            await refetch()
            onClose()
        }
    }

    return (
        <Drawer
            variant="filter"
            isOpen={isOpen}
            onClose={onClose}
            placement={drawerPlacement}
            closeOnEsc
            closeOnOverlayClick
        >
            <DrawerOverlay />
            <DrawerContent>
                <DrawerHeader {...styles?.header}>
                    <Flex {...styles?.titleContainer}>
                        <Text {...styles?.headerText} w="100%">
                            {isSingleSelect ? (
                                <FormattedMessage
                                    id="cart.free_item_promo_drawer.header_title"
                                    defaultMessage="Select a free product!"
                                />
                            ) : (
                                <FormattedMessage
                                    id="cart.free_item_promo_drawer.header_title_plural"
                                    defaultMessage="Select {count} free products!"
                                    values={{count: allowedItems}}
                                />
                            )}
                        </Text>
                        <CloseIcon
                            data-testid="close-icon"
                            onClick={onClose}
                            {...styles?.closeButton}
                        />
                    </Flex>
                </DrawerHeader>

                <DrawerBody {...styles?.body}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : isSingleSelect && promoProducts?.length > 0 ? (
                        <RadioGroup
                            value={thisPromoItems[0]?.id || ''}
                            width="100%"
                            onChange={(val) => {
                                const p = promoProducts.find((pp) => pp.id === val)
                                if (p) handleSelect(p)
                            }}
                        >
                            <Stack gap={remCalc(16)} paddingTop={remCalc(16)}>
                                {promoProducts.map((product) => (
                                    <Flex
                                        key={`${promotionId}-${product.id}`}
                                        onClick={(e) => {
                                            if (e?.target?.closest('[data-stop-propagation]'))
                                                return
                                            handleSelect(product)
                                        }}
                                    >
                                        <FreeItemPromoTile
                                            product={product}
                                            actionButton={
                                                <Radio
                                                    value={product.id}
                                                    display="inline-flex"
                                                    height={{base: '44px', lg: '24px'}}
                                                    borderWidth={remCalc(0.6)}
                                                    isChecked={thisPromoItems.some(
                                                        (item) => item.id === product.id,
                                                    )}
                                                    data-stop-propagation
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            }
                                        />
                                    </Flex>
                                ))}
                            </Stack>
                        </RadioGroup>
                    ) : (
                        <CheckboxGroup
                            value={thisPromoItems.map((item) => item.id) || []}
                            width="100%"
                        >
                            <Stack gap={remCalc(16)} paddingTop={remCalc(16)} width="100%">
                                {promoProducts.map((product) => (
                                    <Flex
                                        key={`${promotionId}-${product.id}`}
                                        onClick={(e) => {
                                            if (e?.target?.closest('[data-stop-propagation]'))
                                                return
                                            handleSelect(product)
                                        }}
                                    >
                                        <FreeItemPromoTile
                                            product={product}
                                            isGrey={
                                                !thisPromoItems.some(
                                                    (item) => item.id === product.id,
                                                ) && thisPromoItems.length === allowedItems
                                            }
                                            actionButton={
                                                <Checkbox
                                                    data-stop-propagation
                                                    value={product.id}
                                                    isChecked={thisPromoItems.some(
                                                        (item) => item.id === product.id,
                                                    )}
                                                    onChange={() => handleSelect(product)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    isDisabled={
                                                        !thisPromoItems.some(
                                                            (item) => item.id === product.id,
                                                        ) && thisPromoItems.length >= allowedItems
                                                    }
                                                />
                                            }
                                        />
                                    </Flex>
                                ))}
                            </Stack>
                        </CheckboxGroup>
                    )}
                </DrawerBody>

                <DrawerFooter display="block" width="full" height="auto">
                    <Stack>
                        <Button
                            width="full"
                            variant="primary"
                            isDisabled={(thisPromoItems?.length || 0) < allowedItems}
                            onClick={handleAddGiftToCart}
                        >
                            <FormattedMessage
                                id="cart.free_item_promo_drawer.footer.button_text"
                                defaultMessage="Return to the cart"
                            />
                        </Button>
                    </Stack>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    )
}

export default FreeItemPromoDrawer

FreeItemPromoDrawer.propTypes = {
    onClose: PropTypes.func,
    isOpen: PropTypes.bool,
    promoProducts: PropTypes.array,
    setSelectedFreeItems: PropTypes.func,
    selectedPromotion: PropTypes.object,
    selectedFreeItems: PropTypes.object,
    basketItems: PropTypes.array,
    basketId: PropTypes.string,
    handleRemoveItem: PropTypes.func,
}
